/**
 * index.html trae etiquetas fijas de Plaxter (descripción, canónica, OG).
 * React Helmet añade las suyas pero NO borra esas fijas, así que un buscador
 * o un navegador podría leer la primera (la de la portada) en el micrositio
 * de un restaurante. Aquí sobreescribimos temporalmente sólo las etiquetas
 * fijas mientras el micrositio está montado, y las restauramos al salir.
 */

export interface StaticHeadValues {
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
}

type Restore = () => void;

const META_SELECTORS: Array<[keyof StaticHeadValues, string]> = [
  ['description', 'meta[name="description"]'],
  ['ogTitle', 'meta[property="og:title"]'],
  ['ogDescription', 'meta[property="og:description"]'],
  ['ogImage', 'meta[property="og:image"]'],
  ['ogUrl', 'meta[property="og:url"]'],
  ['ogTitle', 'meta[name="twitter:title"]'],
  ['ogDescription', 'meta[name="twitter:description"]'],
  ['ogImage', 'meta[name="twitter:image"]'],
];

/** Devuelve la función que restaura los valores originales. */
export function applyStaticHeadOverride(doc: Document, values: StaticHeadValues): Restore {
  const undo: Restore[] = [];

  for (const [key, selector] of META_SELECTORS) {
    const elements = Array.from(doc.head.querySelectorAll<HTMLMetaElement>(selector))
      .filter((element) => !element.hasAttribute('data-rh'));
    for (const element of elements) {
      const previous = element.getAttribute('content');
      element.setAttribute('content', values[key]);
      undo.push(() => {
        if (previous === null) element.removeAttribute('content');
        else element.setAttribute('content', previous);
      });
    }
  }

  const canonicals = Array.from(doc.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'))
    .filter((element) => !element.hasAttribute('data-rh'));
  for (const element of canonicals) {
    const previous = element.getAttribute('href');
    element.setAttribute('href', values.canonical);
    undo.push(() => {
      if (previous === null) element.removeAttribute('href');
      else element.setAttribute('href', previous);
    });
  }

  return () => {
    for (const restore of undo) restore();
  };
}
